import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MemberSpace } from '../lib/api';
import { fetchMySpaces } from '../lib/api';
import { spaceListTitle } from '../lib/spaceIdentity';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { useSelectedSpace } from './SelectedSpaceContext';

interface SpacesListProps {
  onSelectSpace?: (spaceId: string) => void;
}

export function SpacesList({ onSelectSpace }: SpacesListProps) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me', 'spaces'],
    queryFn: fetchMySpaces,
  });

  if (isLoading) {
    return (
      <Skeleton
        className="spaces-list-loading"
        ariaLabel="Loading spaces"
        rows={4}
        variant="list"
      />
    );
  }

  if (isError) {
    return (
      <div className="spaces-list-error" role="alert">
        Could not load spaces ({String(error)}).
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        className="spaces-list-empty"
        icon="🛠️"
        title="No spaces yet"
        description="Mint an invite to start a collaboration with a coworker or AI agent."
      />
    );
  }

  return <SpacesListBody spaces={data} onSelectSpace={onSelectSpace} />;
}

interface SpacesListBodyProps {
  spaces: MemberSpace[];
  onSelectSpace?: (spaceId: string) => void;
}

function SpacesListBody({ spaces, onSelectSpace }: SpacesListBodyProps) {
  const { selectedSpaceId, setSelectedSpaceId } = useSelectedSpace();
  const listRef = useRef<HTMLUListElement | null>(null);

  function handleKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    const list = listRef.current;
    if (!list) return;
    const buttons = Array.from(
      list.querySelectorAll<HTMLButtonElement>('button.space-link'),
    );
    if (buttons.length === 0) return;
    const activeIndex = buttons.findIndex((b) => b === document.activeElement);
    let nextIndex: number;
    if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = buttons.length - 1;
    else if (e.key === 'ArrowDown')
      nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % buttons.length;
    else
      nextIndex =
        activeIndex < 0
          ? buttons.length - 1
          : (activeIndex - 1 + buttons.length) % buttons.length;
    buttons[nextIndex].focus();
  }

  return (
    <ul
      ref={listRef}
      className="spaces-list"
      aria-label="Spaces list"
      onKeyDown={handleKeyDown}
    >
      {spaces.map((space) => (
        <SpaceRow
          key={space.room_id}
          space={space}
          isSelected={selectedSpaceId === space.room_id}
          onSelect={() => {
            setSelectedSpaceId(space.room_id);
            onSelectSpace?.(space.room_id);
          }}
        />
      ))}
    </ul>
  );
}

interface SpaceRowProps {
  space: MemberSpace;
  isSelected: boolean;
  onSelect: () => void;
}

function SpaceRow({ space, isSelected, onSelect }: SpaceRowProps) {
  const isUnread = space.unread_count > 0;
  return (
    <li
      className={`space-row ${isUnread ? 'space-row-unread' : ''} ${isSelected ? 'space-row-selected' : ''}`}
      data-state={space.state}
    >
      <button
        type="button"
        className="space-link"
        onClick={onSelect}
        aria-current={isSelected ? 'page' : undefined}
      >
        <span className="space-description">{spaceListTitle(space.name, space.description)}</span>
        {/* Unread = bold title (CSS on .space-row-unread) + an accent dot. The red
            count badge is retired (ruled do-not: no badge counts, and red means
            error — 26 unread is not an error). T-0200. */}
        {isUnread && <span className="space-unread-dot" aria-label="Unread messages" />}
      </button>
    </li>
  );
}
